/* eslint-disable no-param-reassign */
/* eslint-disable prefer-destructuring */
/* eslint-disable react/destructuring-assignment */
import { useEffect, useRef, useState } from "react"
import EditIcon from "@mui/icons-material/Edit"
import VisibilityIcon from "@mui/icons-material/Visibility"
import { Alert, AlertTitle, Box, Button, Icon } from "@mui/material"
import Fab from "@mui/material/Fab"
import { useTranslation } from "react-i18next"
import { PageConfig, CwComponentConfig } from "../../models/pageConfig"
import DialogService from "../../services/dialog"
import ComponentsUtils from "../../utils/components"
import UIService from "../../services/UIService"
import { Color } from "../../models/colors"
import { DialogType } from "../../models/dialog"
import CwButton from "./cw-button"
import CwBlockUI from "./cw-blockUI"
import FormFieldService from "../../services/formField"
import FormService from "../../services/form"

const CwForm = (
    props: PageConfig<CwComponentConfig.Form> & {
        onEditChange?: (value: boolean) => void
        onSubmit?: () => void
    }
): React.JSX.Element => {
    const { name } = props
    const formConfig: CwComponentConfig.Form | undefined = props?.config
    const useConfigListener: boolean = props?.config
        .useConfigListener as boolean
    const readOnly = !!formConfig?.readOnly
    const showFabButton = !!(
        typeof props.config?.showFabButton === "undefined" ||
        props.config?.showFabButton
    )
    const className = formConfig?.className ? formConfig.className : ""
    const initialValues = useRef(formConfig?.initialValues)
    const fieldsState = useRef<any>({})
    const stateRef = useRef<{
        isValid: boolean
        errors: Array<string>
        edit: boolean
        isLoading: boolean
    }>({
        isValid: formConfig.buttonConfig?.enabledSubmitButton || false,
        errors: [],
        edit: formConfig?.mode !== "show",
        isLoading: true,
    })
    const [blockForm, setBlockForm] = useState<boolean>(false)

    const [state, setState] = useState<{
        isValid: boolean
        errors: Array<string>
        edit: boolean
        isLoading: boolean
    }>(stateRef.current)

    const { t } = useTranslation()

    /**
     * initialiser une nouvelle instance de FormService
     */
    const formService = new FormService(
        name,
        formConfig,
        setState,
        state,
        stateRef,
        initialValues,
        fieldsState,
        props
    )

    // refresh en cas the changement de mode
    useEffect(() => {
        formService.init()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formConfig])

    // exec unique premier lancement
    useEffect(() => {
        let subRefreshId: string | null = null
        let subConfigId: string | null = null
        if (formConfig) {
            FormFieldService.getInstance().subscribe(
                name,
                formService.handleStateChange
            )
            // on s'abonne aux changements externes
            subRefreshId = DialogService.getInstance().subscribe(
                "pageRefesh",
                DialogType.REFRESH,
                name,
                () => {
                    formService.init()
                }
            )
            if (useConfigListener)
                subConfigId = DialogService.getInstance().subscribe(
                    "all",
                    DialogType.CONFIG,
                    name,
                    (params) => {
                        if (typeof params.edit === "boolean")
                            params.edit
                                ? formService.handleEdit()
                                : formService.handleShow()
                    }
                )
        } else {
            UIService.getInstance().showErrorToast(
                "Impossible de charger ce formulaire",
                "CwForm"
            )
        }
        // callback de destruction du composant
        return () => {
            if (subRefreshId)
                DialogService.getInstance().unsubscribe(subRefreshId)
            if (subConfigId)
                DialogService.getInstance().unsubscribe(subConfigId)
            FormFieldService.getInstance().unsubscribe(name)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /**
     * render l'icon soit SHOW/EDIT en fonction du mode
     */
    const renderIcon = () => {
        if (!state.edit)
            return (
                <Fab
                    onClick={formService.handleEdit}
                    className="formModeIcon"
                    aria-label="edit"
                    style={{
                        background: UIService.getColorByName(Color.blue),
                        color: "white",
                    }}
                >
                    <EditIcon />
                </Fab>
            )
        return (
            <Fab
                onClick={formService.handleShow}
                className="formModeIcon"
                aria-label="show"
                style={{
                    background: UIService.getColorByName(Color.blue),
                    color: "white",
                }}
            >
                <VisibilityIcon />
            </Fab>
        )
    }

    /**
     * render le bouton cancel (annuler) en fonction du mode
     */
    const renderCancelButton = () => {
        if (formConfig?.buttonConfig?.showCancelButton)
            return (
                <Button
                    onClick={formService.handleShow}
                    size="large"
                    variant="contained"
                    className="buttonEdit"
                >
                    {t("cancel")}
                </Button>
            )

        return null
    }
    /**
     * render  bouton option (option de plus de form) en fonction du mode
     */
    const renderOptionButton = () => {
        if (formConfig?.buttonConfig?.optionButton)
            return (
                <CwButton
                    name="optionButton"
                    type="button"
                    config={formConfig?.buttonConfig?.optionButton}
                />
            )

        return null
    }

    return state.isLoading ? (
        ComponentsUtils.renderLoader()
    ) : (
        <>
            <CwBlockUI blocking={blockForm} className="fullWidth fullHeight" />
            <form
                className={className}
                onSubmit={(e) =>
                    formService.submit(e, initialValues.current, setBlockForm)
                }
            >
                {!readOnly && showFabButton ? renderIcon() : null}
                {formService.injectFields()}
                {state.edit ? (
                    <>
                        {state.errors.length > 0 ? (
                            <Box className="maxWidthUltraLarges">
                                <Alert className="mt-1" severity="error">
                                    <AlertTitle>
                                        {t("ERR_Form_Errors")}
                                    </AlertTitle>
                                    {t("MSG_Check_Field")} —{" "}
                                    <strong>
                                        {formService
                                            .renderErrorFieldName()
                                            .join(", ")}
                                    </strong>
                                </Alert>
                            </Box>
                        ) : null}
                        {formConfig.buttonConfig ? (
                            <Box className="formButtonBox">
                                <Box className="formButtonBox flexWrap">
                                    <Button
                                        type="submit"
                                        size="large"
                                        variant="contained"
                                        className={formService.getButtonClassName()}
                                        disabled={!state.isValid}
                                    >
                                        {formConfig.buttonConfig
                                            ?.submitButtonIcon &&
                                        formConfig.buttonConfig
                                            .submitButtonIconPosition !==
                                            "end" ? (
                                            <Icon className="mr-1">
                                                {
                                                    formConfig.buttonConfig
                                                        .submitButtonIcon
                                                }
                                            </Icon>
                                        ) : null}
                                        {formService.renderButtonText(
                                            formConfig?.buttonConfig
                                                ?.submitButtonText
                                                ? formConfig.buttonConfig
                                                      .submitButtonText
                                                : "{{#lang.submit}}"
                                        )}
                                        {formConfig.buttonConfig
                                            ?.submitButtonIcon &&
                                        formConfig.buttonConfig
                                            .submitButtonIconPosition ===
                                            "end" ? (
                                            <Icon className="ml-1">
                                                {
                                                    formConfig.buttonConfig
                                                        .submitButtonIcon
                                                }
                                            </Icon>
                                        ) : null}
                                    </Button>
                                    {renderOptionButton()}
                                    {renderCancelButton()}
                                </Box>
                            </Box>
                        ) : null}
                    </>
                ) : null}
            </form>
        </>
    )
}

export default CwForm
